/* eslint-disable class-methods-use-this */
import jwtDecode from 'jwt-decode';
import { resolveBrandCountry } from '../branding/common/utils/resolveBrandCountry';
import { config } from '../config';
import { BankType, BrandType, Country, DebtManager } from '../generated/graphql';
import { storage } from './storage';

const WITH_REFRESH = config.forceRefresh || config.isDevelopment || /localhost/.test(window.location.href);
const REFRESH_TOKEN_KEY = '__RTK';
const ADMIN_TOKEN_STORAGE_KEY = 'useLocalStorage_localAdminInfo';

type JWTPayload = {
  id: string,
  bank: BankType,
  brand: BrandType,
  debtManager: DebtManager,
  country: Country,
  analytics: { deliveryId: string },
  payload?: unknown;
};

let base = '';
const setBase = (value: string) => { base = value; };
export const getBase = () => base;

class Session {
  private _accessToken?: string;

  private _identityValidated = false;

  private _deliveryId = '';

  private _userId = '';

  private _brand = BrandType.Tranquiapp;

  private _debtManager?: DebtManager;

  private _bank?: BankType;

  private _country?: Country;

  private _payload?: unknown;

  get accessToken() {
    return this._accessToken;
  }

  get headers() {
    return {
      Authorization: this._accessToken ? `Bearer ${this._accessToken}` : '',
    };
  }

  get refreshToken() {
    return storage.getItem(REFRESH_TOKEN_KEY);
  }

  get identityValidated() {
    return this._identityValidated;
  }

  get userId() {
    return this._userId;
  }

  get brand() {
    return this._brand;
  }

  get debtManager() {
    return this._debtManager;
  }

  get bank() {
    return this._bank;
  }

  get deliveryId() {
    return this._deliveryId;
  }

  get country() {
    return this._country;
  }

  private decodeToken(token: string) {
    const {
      id,
      bank,
      brand,
      debtManager,
      country,
      analytics,
      payload,
    } = jwtDecode(token) as JWTPayload;

    return { id, bank, brand, debtManager, country, analytics, payload };
  }

  private removeAdminToken() {
    storage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  }

  setAccessToken(token: string) {
    this._accessToken = token;
    const {
      id,
      bank,
      brand,
      debtManager,
      country = Country.Co,
      analytics,
      payload,
    } = this.decodeToken(token);
    this._userId = id || '';
    this._identityValidated = Object.values(BankType).includes(bank);
    this._brand = brand;
    this._debtManager = debtManager;
    this._bank = bank;
    this._deliveryId = analytics.deliveryId;
    this._country = country.toUpperCase() as Country;
    this._payload = payload;
  }

  setRefreshToken(refresh?: string) {
    if (!refresh) {
      this.removeRefreshToken();
    } else {
      storage.setItem(REFRESH_TOKEN_KEY, refresh);
    }
  }

  removeRefreshToken() {
    storage.removeItem(REFRESH_TOKEN_KEY);
  }

  setUserId(id = '') {
    this._userId = id;
  }

  getPayload() {
    if (!(this._payload instanceof Object)) return undefined;
    return this._payload as { debtId: number };
  }

  clear() {
    this._accessToken = undefined;
    this._identityValidated = false;
    this._userId = '';
    this.removeRefreshToken();
    this.removeAdminToken();
  }
}

export const session = new Session();

const simple = async (document: string, authorization: boolean, adminToken?: string) => {
  const body = new URLSearchParams();
  body.append('document', document);
  body.append('authorization', authorization.toString());
  body.append('brand', resolveBrandCountry().brand);
  if (WITH_REFRESH) body.append('withRefresh', 'true');

  const myHeaders = new Headers();
  if (adminToken) {
    myHeaders.append('admin-token', adminToken);
  }

  const raw = await fetch(`${base}/auth/simple`, {
    method: 'POST',
    credentials: 'include',
    headers: myHeaders,
    body,
  });
  const res = await raw.json();
  session.setAccessToken(res.access);
  session.setRefreshToken(res.refresh);
  return res;
};

const forget = () => {
  session.removeRefreshToken();
  return fetch(`${base}/auth/forget`, {
    method: 'POST',
    credentials: 'include',
  });
};

type RefreshProps = {
  brand?: BrandType,
  debtManager?: DebtManager,
  bank?: BankType,
};

const refresh = async (
  props?: RefreshProps,
) => {
  const { brand, debtManager, bank } = props || {};
  return fetch(`${base}/auth/refresh`, {
    method: 'POST',
    body: bank ? JSON.stringify({ brand, bank, debtManager }) : undefined,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
  });
};

const refreshAndSet = async (props?: RefreshProps) => {
  const raw = await refresh(props);
  const res = await raw.json();
  session.setAccessToken(res.access);
  session.setRefreshToken(res.refresh);
  return !!res.access;
};

export {
  forget,
  refresh,
  refreshAndSet, setBase,
  simple,
};
